import { useCallback, useEffect, useRef } from 'react';

/**
 * Encapsulates drag-to-resize logic for the doc/chat split.
 * Returns chatHeight (%) and an onDragStart handler for the drag handle.
 */
export function useChatDragResize(
  containerRef: React.RefObject<HTMLDivElement | null>,
  chatHeight: number,
  setChatHeight: (h: number) => void,
) {
  const dragState = useRef<{
    dragging: boolean;
    startY: number;
    startHeight: number;
  } | null>(null);

  const onDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragState.current = {
        dragging: true,
        startY: e.clientY,
        startHeight: chatHeight,
      };
    },
    [chatHeight],
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragState.current?.dragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerHeight = containerRect.height;
      if (containerHeight === 0) return;

      const deltaY = dragState.current.startY - e.clientY;
      const deltaPct = (deltaY / containerHeight) * 100;
      const newHeight = Math.min(
        80,
        Math.max(20, dragState.current.startHeight + deltaPct),
      );
      setChatHeight(newHeight);
    };

    const onMouseUp = () => {
      if (dragState.current) {
        dragState.current = null;
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [containerRef, setChatHeight]);

  return { onDragStart };
}
